import React, {useState} from 'react';
import {Icon, Image} from 'semantic-ui-react';
import {Field, Formik, FormikProps, FormikHelpers} from 'formik';

import {Form, Button, Modal, Table, Pagination} from '@components';
import constants from '@constants';
import mappings from '@mappings';
import utils from '@utils';
import validate from '@validations';
import {Props} from './types';
import * as handlers from './handlers';
import {makeColumns} from './Columns';
import * as hooks from './hooks';

import './index.scss';

const Dashboard: React.FC<Props> = props => {
  const [loading, setLoading] = useState(true);
  const [isPaging, setIsPaging] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [isResend, setIsResend] = useState(false);
  const [isFailedSendModal, setIsFailedSendModal] = useState(false);

  hooks.useMount(props, {setLoading});

  const initialValues = {
    // デフォルトの日付は当日
    currentDate: `${utils.date.getNow('YYYY/MM/DD')}`,
    // デフォルトの初期値は3日
    traceDuration: constants.DATES.THIRD_DAY,
    userNames: [],
    userName: '',
    organization: '',
    organizations: [],
    position: '',
    positions: [],
    isSafe: false,
    isMinorInjury: false,
    isSeriousInjury: false,
    unAnswered: false,
    answered: false,
    isSuccess: false,
    isFailed: false,
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={(values: any, formikHelpers: FormikHelpers<any>) => {
          handlers.search(values, formikHelpers, props, setLoading);
        }}
        validate={validate.dashboard.main}
      >
        {(formProps: FormikProps<any>) => {
          return (
            <>
              <div className="a__dashboard">
                <div className="b__title">安否回答状況</div>
                <div className="w__recent-information">
                  <div className="b__recent-time">
                    <div className="b__label">
                      <b>
                        <i>{`${props.notifications.detail.recentSearchDatetime}`}</i>
                      </b>
                    </div>
                    &nbsp; 時点での回答状況を表示中
                  </div>
                  <Button.Normal
                    onClick={() => {
                      handlers.search(
                        formProps.values,
                        formProps,
                        props,
                        setLoading,
                        setIsUpdate
                      );
                    }}
                    rounded
                    color="green"
                    disabled={formProps.isSubmitting || loading || isUpdate}
                    loading={isUpdate}
                  >
                    最新の回答状況を取得する
                  </Button.Normal>
                </div>
                <div className="w__dashboard-header">
                  <div className="w__field date">
                    <Field
                      component={Form.DatePicker}
                      name="currentDate"
                      dark
                      isSearch
                      me={props.me}
                      actions={props.actions}
                      notifications={props.notifications}
                      isAutoOpen={false}
                    />
                  </div>
                  <div className="b__text">から</div>
                  <div className="w__field">
                    <Field
                      component={Form.Dropdown}
                      name="traceDuration"
                      options={utils.mappings.createSelections(mappings.dates)}
                      size="sm"
                      mode="find"
                      me={props.me}
                      actions={props.actions}
                      notifications={props.notifications}
                    />
                  </div>
                  以内で発生した災害安否回答状況を表示
                </div>
                <div className="w__cards">
                  <div className="b__card">
                    <div className="w__card-header">
                      <div className="b__image">
                        <Icon name="user outline" className="b__person" />
                      </div>
                      <div className="b__title-text">送信対象</div>
                    </div>
                    <div className="b__divider" />
                    <div className="b__card-content">
                      {props.notifications.detail.sentExecutionCount}&nbsp;
                      <span className="small-text">件</span>
                    </div>
                  </div>
                  <div className="b__card">
                    <div className="w__card-header">
                      <div className="b__image">
                        <Image src={constants.IMAGES.SENDING_MAIL} />
                      </div>
                      <div className="b__title-text">送信完了</div>
                    </div>
                    <div className="b__divider" />
                    <div className="b__card-content">
                      {props.notifications.detail.sentSuccessCount}&nbsp;
                      <span className="small-text">件</span>
                    </div>
                  </div>
                  <div className="b__card">
                    <div className="w__card-header">
                      <div className="b__image">
                        <Image src={constants.IMAGES.CHECK_ROUNDED} />
                      </div>
                      <div className="b__title-text">安否確認済み</div>
                    </div>
                    <div className="b__divider" />
                    <div className="b__card-content">
                      {props.notifications.detail.replyCount}&nbsp;
                      <span className="small-text">件</span>
                    </div>
                  </div>
                  <div className="b__card">
                    <div className="w__card-header">
                      <div className="b__image">
                        <Image src={constants.IMAGES.TIMER} />
                      </div>
                      <div className="b__title-text">回答待ち</div>
                    </div>
                    <div className="b__divider" />
                    <div className="b__card-content">
                      {props.notifications.detail.noReplyCount}&nbsp;
                      <span className="small-text">件</span>
                    </div>
                  </div>
                  <div className="b__card">
                    <div className="w__card-header">
                      <div className="b__image">
                        <Image src={constants.IMAGES.AMBULANCE} />
                      </div>
                      <div className="b__title-text">怪我人</div>
                    </div>
                    <div className="b__divider" />
                    <div className="b__card-content">
                      {props.notifications.detail.personsInjured}&nbsp;
                      <span className="small-text">人</span>
                    </div>
                  </div>
                </div>
                <div className="w__search-field">
                  <div className="w__safety-search">
                    <div className="w__search">
                      <div className="w__row">
                        <div className="w__input-search name">
                          <Form.Label text="名前" />
                          <div className="w__input">
                            <Field
                              component={Form.Input}
                              name="userName"
                              color="white"
                              placeholder="名前"
                            />
                            <Button.Icon
                              name="plus"
                              color="yellow"
                              onClick={() => {
                                handlers.addSearchWord(
                                  formProps,
                                  'userName',
                                  'userNames'
                                );
                              }}
                            />
                          </div>
                          {formProps.values.userNames.length ? (
                            <div className="w__search-words">
                              <Form.SearchArea
                                searchWords={formProps.values.userNames}
                                name="userNames"
                                formProps={formProps}
                              />
                            </div>
                          ) : (
                            <div className="w__search-words" />
                          )}
                        </div>

                        <div className="w__checkbox-field">
                          <Form.Label text="怪我" />
                          <div className="w__checkboxes">
                            <Field
                              component={Form.Checkbox}
                              name="isSafe"
                              label="無事"
                            />
                            <div className="w__checkbox-row">
                              <Field
                                component={Form.Checkbox}
                                name="isMinorInjury"
                                label="軽傷"
                              />
                              <Field
                                component={Form.Checkbox}
                                name="isSeriousInjury"
                                label="重傷"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="w__checkbox-field">
                          <Form.Label text="回答状況" />
                          <div className="w__checkboxes">
                            <div className="w__checkbox-row none">
                              <Field
                                component={Form.Checkbox}
                                name="unAnswered"
                                label="未回答"
                              />
                              <Field
                                component={Form.Checkbox}
                                name="answered"
                                label="回答済み"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="w__row lower">
                      <div className="w__checkbox-field">
                        <Form.Label text="メール送信状況" />
                        <div className="w__checkboxes">
                          <div className="w__checkbox-row none">
                            <Field
                              component={Form.Checkbox}
                              name="isSuccess"
                              label="送信完了"
                            />
                            <Field
                              component={Form.Checkbox}
                              name="isFailed"
                              label="送信失敗"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="w__search">
                        <div className="w__input-search">
                          <Form.Label text="所属組織" />
                          <div className="w__input">
                            <Field
                              component={Form.Input}
                              name="organization"
                              placeholder="所属組織"
                              color="white"
                            />
                            <Button.Icon
                              name="plus"
                              color="yellow"
                              onClick={() => {
                                handlers.addSearchWord(
                                  formProps,
                                  'organization',
                                  'organizations'
                                );
                              }}
                            />
                          </div>
                          {formProps.values.organizations.length ? (
                            <div className="w__search-words">
                              <Form.SearchArea
                                searchWords={formProps.values.organizations}
                                name="organizations"
                                formProps={formProps}
                              />
                            </div>
                          ) : null}
                        </div>

                        <div className="w__input-search">
                          <Form.Label text="役職" />
                          <div className="w__input">
                            <Field
                              component={Form.Input}
                              name="position"
                              placeholder="役職"
                              color="white"
                            />
                            <Button.Icon
                              name="plus"
                              color="yellow"
                              onClick={() => {
                                handlers.addSearchWord(
                                  formProps,
                                  'position',
                                  'positions'
                                );
                              }}
                            />
                          </div>
                          {formProps.values.positions.length ? (
                            <div className="w__search-words">
                              <Form.SearchArea
                                searchWords={formProps.values.positions}
                                name="positions"
                                formProps={formProps}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="w__search-button">
                      <Button.Normal
                        rounded
                        color="yellow"
                        text="検索する"
                        onClick={() => {
                          handlers.search(
                            formProps.values,
                            formProps,
                            props,
                            setLoading,
                            setIsSearch
                          );
                        }}
                        disabled={formProps.isSubmitting || loading}
                        loading={isSearch}
                      />
                    </div>
                  </div>
                </div>

                <div className="w__body-buttons">
                  <div className="b__count">
                    合計&nbsp;
                    <span className="count">
                      {Number(
                        props.notifications.detail.pagination.totalCount
                      ).toLocaleString()}
                    </span>
                    &nbsp;件
                  </div>
                  <div className="b__link-button">
                    <Button.Normal
                      color="green"
                      rounded
                      onClick={() => setIsResend(true)}
                      disabled={
                        props.notifications.detail.sentServerErrorCount === 0
                      }
                    >
                      <Icon name="redo alternate" className="b__icon" />
                      <span className="inner-text">
                        送信失敗のユーザーに再送信する
                      </span>
                    </Button.Normal>

                    <Button.Normal
                      className="link-button"
                      color="green"
                      rounded
                      onClick={() => setIsModal(true)}
                      disabled={props.notifications.detail.noReplyCount === 0}
                    >
                      <Icon name="paper plane" className="b__icon" />
                      <span className="inner-text">
                        未回答のユーザーに再送信する
                      </span>
                    </Button.Normal>
                  </div>
                </div>
                <div className="b__table">
                  <Table.Main
                    columns={makeColumns(props, setLoading, setIsPaging)}
                    data={props.notifications.detail.pagination.data.active}
                    loading={formProps.isSubmitting || loading}
                    emptyMessage="回答情報がありません"
                  />
                  <Pagination
                    totalCount={
                      props.notifications.detail.pagination.totalCount
                    }
                    current={props.notifications.detail.pagination.current}
                    updateCurrent={async (current: number) => {
                      await handlers.updateCurrent(props, current, setIsPaging);
                    }}
                    disabled={formProps.isSubmitting || isPaging}
                  />
                </div>
              </div>
              <Modal.Resend
                setIsFailedSendModal={setIsFailedSendModal}
                match={props.match}
                notification={props.notifications.detail}
                isModal={isModal}
                handleCloseModal={setIsModal}
                title="未回答のユーザーに再送信"
                me={props.me}
                disabled={props.notifications.detail.noReplyCount === 0}
                message={
                  <>
                    <div>
                      安否確認に未回答のユーザー&nbsp; (
                      <b>{props.notifications.detail.noReplyCount}人</b>)&nbsp;
                      に安否確認を再送信します。
                    </div>
                    <div>通知手段を確認してから再送信を行なってください。</div>
                  </>
                }
              />
              <Modal.Resend
                setIsFailedSendModal={setIsFailedSendModal}
                notification={props.notifications.detail}
                isModal={isResend}
                handleCloseModal={setIsResend}
                title="送信失敗のユーザーに再送信"
                me={props.me}
                disabled={props.notifications.detail.sentServerErrorCount === 0}
                isFailed
                match={props.match}
                message={
                  <>
                    <div>
                      送信失敗でユーザーへの送信が失敗したユーザー&nbsp;(
                      <b>{props.notifications.detail.sentServerErrorCount}人</b>
                      ) &nbsp; に安否確認を再送信します。
                    </div>
                    <div>通知手段を確認してから再送信を行なってください。</div>
                  </>
                }
              />
              <Modal.FailedCalling
                isModal={isFailedSendModal}
                handleCloseModal={setIsFailedSendModal}
              />
            </>
          );
        }}
      </Formik>
    </>
  );
};
export default Dashboard;
